import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  getIpInfo,
  countries,
  countries_fr,
  countryTimezones,
  defaultTimezones,
  formatTimezone,
  IpInfo,
} from "@/utils/ip-detection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Globe, Clock, Building } from "lucide-react";
import { BASE_URL } from '@/config/api';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "@/components/ui/tabs";

const AccountCheck: React.FC = () => {
  const navigate = useNavigate();
  const { t, locale } = useLanguage();
  const { user, refreshUserData } = useAuth();

  const [country, setCountry] = useState<string>('US');
  const [timezone, setTimezone] = useState<string>('');
  const [ipInfo, setIpInfo] = useState<IpInfo | null>(null);
  const [isLoadingIp, setIsLoadingIp] = useState(true);
  const [availableTimezones, setAvailableTimezones] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAccountType, setSelectedAccountType] = useState<'Coach' | 'School'>('Coach');
  const [schoolName, setSchoolName] = useState<string>('');

  const selectedCountries = locale === "fr" ? countries_fr : countries;

  useEffect(() => {
    const fetchIpInfo = async () => {
      setIsLoadingIp(true);
      try {
        const ipData = await getIpInfo();
        setIpInfo(ipData);

        const detectedCountry = ipData.countryCode || 'US';
        setCountry(detectedCountry);

        const detectedTzList = countryTimezones[detectedCountry] || defaultTimezones;
        let detectedTimezone = "";
        try {
          const sysTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          detectedTimezone = detectedTzList.includes(sysTz) ? sysTz : detectedTzList[0];
        } catch {
          detectedTimezone = detectedTzList[0];
        }
        setTimezone(detectedTimezone);
        setAvailableTimezones(detectedTzList);
      } catch (error) {
        setCountry('US');
        setTimezone(defaultTimezones[0]);
        setAvailableTimezones(defaultTimezones);
      } finally {
        setIsLoadingIp(false);
      }
    };

    fetchIpInfo();
  }, []);

  useEffect(() => {
    const tzList = countryTimezones[country] || defaultTimezones;
    setAvailableTimezones(tzList);

    if (!tzList.includes(timezone)) {
      let nextTimezone = "";
      try {
        const sysTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        nextTimezone = tzList.includes(sysTz) ? sysTz : tzList[0];
      } catch {
        nextTimezone = tzList[0];
      }
      setTimezone(nextTimezone);
    }
  }, [country]);

  const handleCountryChange = (value: string) => {
    setCountry(value);
  };

  const handleTimezoneChange = (value: string) => {
    setTimezone(value);
  };

  const handleAccountTypeChange = (type: 'Coach' | 'School') => {
    setSelectedAccountType(type);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('auth-token');
      const response = await fetch(`${BASE_URL}/api/user/account/check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : undefined
        },
        body: JSON.stringify({
          country,
          timezone,
          accountType: selectedAccountType,
          schoolName: selectedAccountType === 'School' ? schoolName : undefined
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update account information');
      }

      await refreshUserData();
      navigate('/agenda');
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <form onSubmit={handleSubmit} className="w-full max-w-md p-8 bg-white rounded-lg shadow">
        <h1 className="text-2xl font-bold mb-4">{t('account_check.title', 'Vérification du compte')}</h1>
        <p className="mb-6">{t('account_check.desc', "Veuillez confirmer votre pays et fuseau horaire pour votre compte.")}</p>

        <div className="mb-6">
          <Tabs value={selectedAccountType} onValueChange={handleAccountTypeChange} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="Coach">Coach</TabsTrigger>
              <TabsTrigger value="School">School</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {selectedAccountType === 'School' && (
          <div className="mb-4">
            <label className="block font-medium mb-2" htmlFor="schoolName">
              {t('register.schoolName', 'School Name')} *
            </label>
            <div className="relative">
              <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
              <Input
                id="schoolName"
                name="schoolName"
                type="text"
                value={schoolName}
                onChange={(e) => setSchoolName(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        )}

        <div className="mb-4">
          <label className="block font-medium mb-2" htmlFor="country">{t('common.country', 'Pays')}</label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
            <Select value={country} onValueChange={handleCountryChange}>
              <SelectTrigger className="w-full pl-10">
                <SelectValue placeholder={t('common.country', 'Pays')} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {selectedCountries.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            {isLoadingIp && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        </div>

        <div className="mb-6">
          <label className="block font-medium mb-2" htmlFor="timezone">{t('common.timezone', 'Fuseau horaire')}</label>
          <div className="relative">
            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
            <Select value={timezone} onValueChange={handleTimezoneChange}>
              <SelectTrigger className="w-full pl-10">
                <SelectValue placeholder={t('common.timezone', 'Fuseau horaire')} />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectGroup>
                  {availableTimezones.map((tz) => (
                    <SelectItem key={tz} value={tz}>{formatTimezone(tz)}</SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={isSubmitting || timezone === '' || !availableTimezones.includes(timezone)}
        >
          {isSubmitting
            ? t('common.loading', 'Chargement...')
            : t('account_check.confirm', 'Confirmer les informations')}
        </Button>
      </form>
    </div>
  );
};

export default AccountCheck;
